import * as React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import { leaveFootprint } from 'ducks/client/footprints';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { checkinReservation } from 'ducks/client/reservation';
import { Reservation } from 'models/reservation';
import { Product } from 'models/product';
import { ReduxState } from 'ducks';
import { CurrentPositionContext } from 'contexts/CurrentPositionContext';
import { GuestTypeCount } from 'contexts/ETicketRedeemTargetContext';
import { useETicketRedemptionErrorReason } from 'hooks/useETicketRedemptionErrorReason';
import { formatTextWithLineBreaks } from 'lib/util/formatTextWithLineBreaks';
import config from 'config';

import styles from './ETicketCheckinModal.module.css';

// Show checkin modal
export const ETicketCheckinModal = ({
  reservation,
  open,
  onClose,
  onSuccessfulCheckin,
  stubKey,
  initialStubOptionKey,
  guestTypeCounts,
  product,
}: {
  reservation: Reservation;
  open: boolean;
  onClose: () => void;
  onSuccessfulCheckin: () => void;
  stubKey?: string;
  initialStubOptionKey?: string;
  guestTypeCounts?: GuestTypeCount[];
  product: Product | undefined;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [stubOptionKey, setStubOptionKey] = React.useState<string>(initialStubOptionKey ?? '');
  const { apiKey } = React.useContext(ApiKeyContext);
  const { i18n } = useTranslation();
  const { currentPosition } = React.useContext(CurrentPositionContext);
  const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);
  const [afterClickRedemptionButton, setAfterClickRedemptionButton] = React.useState<boolean>(
    false
  );
  const [cancelButtonLoading, setCancelButtonLoading] = React.useState<boolean>(false);

  const inFlight = useSelector((state: ReduxState) => state.reservation.inFlight);
  const error = useSelector((state: ReduxState) => state.reservation.error);

  const stubText = React.useMemo(() => {
    const stub = product?.qr_checkin_settings?.stubs?.find((stub) => stub.key === stubKey);
    return stub?.text;
  }, [stubKey, product]);

  const totalRedemptionGuestCount =
    (guestTypeCounts || []).reduce((acc, guestTypeCount) => {
      return acc + guestTypeCount.count;
    }, 0) || 0;

  const redemptionErrorWithGuestTypeCount = totalRedemptionGuestCount !== 0 && showErrorMessage;

  const closeHandler = () => {
    if (redemptionErrorWithGuestTypeCount) {
      window.location.reload();
      setCancelButtonLoading(true);
    } else {
      onClose();
    }
  };

  React.useEffect(() => {
    const onWindowClick = (event: MouseEvent | TouchEvent) => {
      if (modalRef && !modalRef.current?.contains(event.target as Node)) {
        closeHandler();
      }
    };

    window.addEventListener('mousedown', onWindowClick);

    return () => {
      window.removeEventListener('mousedown', onWindowClick);
    };
  }, [showErrorMessage]);

  const { reason } = useETicketRedemptionErrorReason(reservation, product ?? null);

  React.useEffect(() => {
    if (inFlight || !afterClickRedemptionButton) return;

    if (error) {
      setShowErrorMessage(true);
    } else {
      onSuccessfulCheckin();
      onClose();
    }

    setAfterClickRedemptionButton(false); // reset after handling
  }, [inFlight, error]);

  React.useEffect(() => {
    if (stubOptions?.length) {
      setStubOptionKey(stubOptions[0].value);
    }
  }, []);

  const stubOptions = product?.qr_checkin_settings?.stubs
    ?.find((stub) => stub.key === stubKey)
    ?.options?.map((option) => {
      return {
        value: option.key,
        text: option.text,
      };
    });

  const noteForRedemptionModal = product?.qr_checkin_settings?.note_for_redemption_modal;

  console.log('noteForRedemptionModal', noteForRedemptionModal);
  console.log('product', product);

  const handleStubOptionChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setStubOptionKey(event.target.value);

  const computeGuestCount = (): number => {
    if (guestTypeCounts?.length) {
      return guestTypeCounts.reduce((acc, guestTypeCount) => acc + guestTypeCount.count, 0);
    }
    return reservation.guests.length;
  };

  const prepareGuestTypeCountsForDispatch = () => {
    return (guestTypeCounts || []).map((guestTypeCount) => ({
      guest_type_key: guestTypeCount.guestTypeKey,
      count: guestTypeCount.count,
    }));
  };

  const checkinHandler = async () => {
    const promises = [
      dispatch(
        checkinReservation(
          apiKey,
          reservation?.id || '',
          computeGuestCount(),
          '',
          '',
          stubKey || '',
          stubOptionKey || '',
          prepareGuestTypeCountsForDispatch(),
          i18n.language
        )
      ),
    ];
    if (stubKey) {
      promises.push(
        dispatch(
          leaveFootprint(apiKey, {
            reservation_id: reservation.id,
            latitude: currentPosition?.lat() ?? 0,
            longitude: currentPosition?.lng() ?? 0,
            stub_key: stubKey,
          })
        )
      );
    }

    try {
      setShowErrorMessage(false);
      setAfterClickRedemptionButton(true);
      await Promise.all(promises);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className={clsx(styles['modal'], open ? styles['is-active'] : null)}>
      <div className={styles['modal__inner']}>
        <div className={styles['modal__content']}>
          <div ref={modalRef} className={styles['c-review__modal__content']}>
            <div className={styles['modal__content__memo']}>
              <p className={styles['modal__content__memo__item']}>{t('Confirmation')}</p>
              {stubText ?? <p className={styles['modal__content__memo__item']}>{stubText}</p>}
            </div>
            {config.enableSplitCheckin && (guestTypeCounts || []).length > 0 && (
              <>
                <p className={styles['modal__content__number']}>
                  {(guestTypeCounts || [])
                    .filter((guestTypeCount) => guestTypeCount.count > 0)
                    .map((guestTypeCount, index) => (
                      <>
                        {guestTypeCount.guestTypeTitle} : {guestTypeCount.count}
                        {index !== (guestTypeCounts || []).length - 1 && <br />}
                      </>
                    ))}
                </p>
                <div className={styles['modal__content__summary']}>
                  <p>{t('Total')}</p>
                  <p>
                    <span>{totalRedemptionGuestCount}</span>
                    {t('people')}
                  </p>
                </div>
              </>
            )}
            {config.enableETicketUXImprovements ? (
              <>
                <p className={clsx(styles['modal__content__txt'], styles['center'])}>
                  {reason ? (
                    <>{reason}</>
                  ) : (
                    <>
                      {noteForRedemptionModal ? (
                        <p className={clsx(styles['custom_note'])}>{noteForRedemptionModal}</p>
                      ) : (
                        <>
                          {t('You are about to redeem ticket.')}
                          <br />
                          {t('Click "Redeem" to proceed')}
                        </>
                      )}
                    </>
                  )}
                </p>
              </>
            ) : (
              <>
                <p className={clsx(styles['modal__content__txt'], styles['center'])}>
                  {reason ? (
                    <>{reason}</>
                  ) : (
                    <>
                      {t('You are about to redeem ticket.')}
                      <br />
                      {t('Click "Redeem" to proceed')}
                    </>
                  )}
                </p>
                {noteForRedemptionModal && !reason && (
                  <p
                    className={clsx(
                      styles['modal__content__txt'],
                      styles['center'],
                      styles['checkin_modal__note']
                    )}
                  >
                    {noteForRedemptionModal}
                  </p>
                )}
              </>
            )}
            {redemptionErrorWithGuestTypeCount && (
              <p
                className={clsx(
                  styles['modal__content__txt'],
                  styles['center'],
                  styles['checkin_modal__note']
                )}
              >
                {t(
                  'Some of the tickets have been redeemed already and we were not able to redeem tickets for {{guestCount}}. Please reselect the number of guests.',
                  {
                    guestCount: totalRedemptionGuestCount,
                  }
                )}
              </p>
            )}

            {stubOptions?.length && !reason ? (
              <div className={clsx(styles['checkin_modal__option'])}>
                {/* <p className={styles['checkin_modal__label']}>
                  {stubOptions.find((o) => o.value === stubOptionKey)?.text}
                </p> */}
                <label>
                  <select value={stubOptionKey} onChange={handleStubOptionChange}>
                    {stubOptions?.map((stubOption) => (
                      <option key={stubOption.value} value={stubOption.value}>
                        {stubOption.text}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            <>
              {!reason && (
                <div className={styles['modal__content__btns']}>
                  {!redemptionErrorWithGuestTypeCount && (
                    <button
                      className={clsx(styles['btn'], styles['btn--submit'], styles['btn--redeem'])}
                      onClick={() => {
                        checkinHandler();
                      }}
                      disabled={inFlight}
                      style={{
                        ...(product?.qr_checkin_settings?.redemption_button_color && !inFlight
                          ? {
                              backgroundColor:
                                product?.qr_checkin_settings?.redemption_button_color,
                            }
                          : {}),
                        ...(product?.qr_checkin_settings?.redemption_button_text_color && !inFlight
                          ? { color: product?.qr_checkin_settings?.redemption_button_text_color }
                          : {}),
                      }}
                    >
                      {inFlight && (
                        <Box width="100%" position="absolute">
                          <Box width="100%" display="flex" justifyContent="center">
                            <CircularProgress />
                          </Box>
                        </Box>
                      )}
                      {product?.qr_checkin_settings?.redemption_button_text
                        ? formatTextWithLineBreaks(
                            product?.qr_checkin_settings?.redemption_button_text
                          )
                        : t('Redeem')}
                    </button>
                  )}
                  <button
                    className={clsx(styles['btn'], styles['btn--cancel'])}
                    disabled={cancelButtonLoading}
                    onClick={() => {
                      closeHandler();
                    }}
                  >
                    {cancelButtonLoading && (
                      <Box width="100%" position="absolute">
                        <Box width="100%" display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      </Box>
                    )}
                    {t('Close')}
                  </button>
                </div>
              )}
            </>
          </div>
        </div>
      </div>
      <div className={styles['modal__overlay']}></div>
    </div>
  );
};
